import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Spinner, VerticalFlowCanvas, HintCards } from '@chatsdet/ui';
import { ApiSequencePanel, type ApiSequenceActions } from '../api';
import { FlowLineageCanvas } from '../lineage/FlowLineageCanvas';
import { FlowSpecView } from '../spec/FlowSpecView';
import { useFlowSpec } from '../spec/useFlowSpec';
import type { SpecWorkflowMeta } from '../spec/buildLiveWorkflow';
import type { WorkflowNode, WorkflowEdge } from '../../../../types/workflow';
import type { WorkflowInputDefinition, WorkflowScenario } from '@chatsdet/types';
import type { CredentialTemplate } from '../../../../hooks/useCredentialTemplates';
import type { NodeTypes, SidebarTab, FlowView } from '../types';
import type { WorkflowStatus } from '../constants';
import { StarterPicker, type AutobuildSubmitPayload } from '../StarterPicker';
import { AutobuildPipelinePanel } from './AutobuildPipelinePanel';
import {
  useFlowAutobuild,
  useAutobuildRetryMutation,
  useAutobuildCancelMutation,
} from '../hooks/useFlowAutobuild';
import { WorkflowSettingsOverlay, type WorkflowSource } from '../WorkflowSettingsOverlay';
import { ScenarioConfigOverlay } from './ScenarioConfigOverlay';
import { PlaybookOverlay } from '../PlaybookOverlay';
import { CacheDebugOverlay } from '../runs/cache-debug/CacheDebugOverlay';
import type { VersionSelection } from '../VersionDropdown';
import type {
  DraftSnapshotDetail,
  DraftSnapshotSummary,
  SnapshotDetail,
  SnapshotSummary,
  WorkflowRevisionDetail,
  WorkflowRevisionSummary,
} from '../hooks/useWorkflowSnapshots';
import { HistoryBanner } from '../HistoryBanner';
import { OptimizingBanner } from '../OptimizingBanner';
import { MaintenanceFeedbackBanner } from '../MaintenanceFeedbackBanner';
import { useMaintenanceFeedbackQuery } from '../hooks/useMaintenanceFeedback';
import { ExplorationBanner } from '../ExplorationBanner';
import { LocalEmailHintBanner } from '../LocalEmailHintBanner';
import { API_URL, IS_LOCAL } from '../../../../config';
import { ValidationBanner } from '../validation/ValidationBanner';
import { GenerationInterstitial } from './GenerationInterstitial';
import { FlowDesignerToolbar, type FlowDesignerToolbarProps } from './FlowDesignerToolbar';
import { FlowDesignerViewControls } from './FlowDesignerViewControls';
import { FlowDesignerEdgeMenu } from './FlowDesignerEdgeMenu';
import {
  deriveSeedDependencies,
  hasSingleApiSequenceExecutableNode,
} from './flowCanvasDerivations';
import { useWorkflowGenerationEvents } from '../hooks/useWorkflowGenerationEvents';
import type { WorkflowNodeType } from '../helpers';
import type { usePlaybookCache } from '../hooks/usePlaybookCache';
import type { ActiveOptimization } from '../hooks/useCacheConvergence';

const AUTOBUILD_PANEL_DISMISSED_KEY_PREFIX = 'autobuild-panel-dismissed:';

/**
 * Reads whether the user already dismissed the Autobuild terminal panel for
 * this workflow on a previous visit. Guarded against SSR (no `window`),
 * privacy-mode/quota-full `localStorage` throwing, and a corrupt value —
 * none of those should crash the designer, they should just fall back to
 * "not dismissed" so the panel shows again.
 */
function readAutobuildPanelDismissed(workflowId: string | null): boolean {
  if (!workflowId) return false;
  try {
    return (
      window.localStorage.getItem(`${AUTOBUILD_PANEL_DISMISSED_KEY_PREFIX}${workflowId}`) === '1'
    );
  } catch {
    return false;
  }
}

/** Persists an Autobuild panel dismissal for this workflow. Best-effort — see `readAutobuildPanelDismissed`. */
function writeAutobuildPanelDismissed(workflowId: string | null): void {
  if (!workflowId) return;
  try {
    window.localStorage.setItem(`${AUTOBUILD_PANEL_DISMISSED_KEY_PREFIX}${workflowId}`, '1');
  } catch {
    // Storage unavailable/full — in-session dismissal state (autobuildDismissed) still works.
  }
}

type FlowDesignerCanvasProps = Omit<FlowDesignerToolbarProps, 'isAuthoring'> & {
  // Canvas
  canvasNodes: WorkflowNode[];
  edges: WorkflowEdge[];
  nodeTypes: NodeTypes;
  isPreviewingHistory: boolean;
  handleCanvasNodeClick: (event: React.MouseEvent, node: WorkflowNode) => void;
  handleCanvasPaneClick: () => void;
  handleVerticalCanvasNodeDelete: (nodeId: string) => void;
  handleEdgeClick: (edgeId: string, edge: WorkflowEdge, event: React.MouseEvent) => void;
  handleConnect: (sourceNodeId: string, targetNodeId: string, edgeType?: string) => void;
  handlePrependNode: (nodeType: string) => void;
  handleNodeReorder: (nodeId: string, newIndex: number, chain: string[]) => void;
  selectedNodeIds: Set<string>;
  handleSelectionChange: (ids: Set<string>) => void;
  handleMultiNodeDelete: (nodeIds: string[]) => void;
  handleMultiNodeReorder: (nodeIds: string[], newStartIndex: number, chain: string[]) => void;
  handleOrphanInsertOnEdge: (nodeId: string, edgeId: string) => void;
  selectedEdgeId: string | null;
  currentStepId: string | null;
  selectedNodeForConfig: WorkflowNode | null;
  workflowStatus: string;
  isGenerating: boolean;

  // Settings and overlays
  isScenarioConfigOpen: boolean;
  setIsScenarioConfigOpen: (v: boolean) => void;
  workflowInputs?: WorkflowInputDefinition[];
  workflowScenarios?: WorkflowScenario[];
  credentialTemplates?: CredentialTemplate[];
  onSaveInputs?: (inputs: WorkflowInputDefinition[]) => void;
  isSavingInputs?: boolean;
  onSaveScenarios?: (scenarios: WorkflowScenario[]) => void;
  isSavingScenarios?: boolean;
  isPlaybookOpen: boolean;
  setIsPlaybookOpen: (v: boolean) => void;
  playbookScrollToNodeId: string | null;
  setPlaybookScrollToNodeId: (v: string | null) => void;
  workflowName: string;
  workflowDescription: string | null;
  workflowPreflight: string | null;
  workflowCleanup: string | null;
  workflowRunAgentically: boolean;
  workflowSeedRunOncePerSuite: boolean;
  workflowCurrentStatus: WorkflowStatus;
  workflowCreatedBy: string | null;
  workflowApprovedBy: string | null;
  workflowApprovedAt: string | null;
  workflowSource: WorkflowSource | null;
  workflowEnvironmentConstraints?:
    | { mode: 'only'; environmentIds: string[] }
    | { mode: 'envTypes'; types: Array<'permanent' | 'ephemeral' | 'local' | 'sandbox'> }
    | null;
  workflowEligibleEnvironmentIds?: string[] | null;
  workflowEnvironments?: Array<{
    id: string;
    name: string;
    type: 'permanent' | 'ephemeral' | 'local' | 'sandbox';
    isDefault: boolean;
  }> | null;
  workflowDefaultEnvironmentId?: string | null;
  workflowPassByEnvironment?: Array<{
    environmentId: string;
    environmentName: string;
    environmentType: 'permanent' | 'ephemeral' | 'local' | 'sandbox';
    everPassed: boolean;
    passed: number;
    total: number;
    health: string;
  }> | null;

  nodes: WorkflowNode[];

  // Inserting spinner
  isInsertingNode: boolean;

  // Starter picker
  handleStandaloneCreateNode: (nodeType: WorkflowNodeType) => void;
  createWorkflowMutationIsPending: boolean;
  hasApiCatalog: boolean;
  hasSandboxActions: boolean;
  hasMcp: boolean;

  // Flow Autobuild (spec -> flow pipeline started from the StarterPicker)
  autobuildEnabled: boolean;
  onAutobuildSubmit: (payload: AutobuildSubmitPayload) => Promise<void>;
  isAutobuildSubmitting: boolean;

  // Edge context menu
  edgeMenuPosition: { x: number; y: number } | null;
  clearEdgeSelection: () => void;
  handleInsertNodeOnEdge: (nodeType: string) => void;
  handleDeleteSelectedEdge: () => void;

  // Cache management (from PlaybookOverlay)
  workflowData: any;
  setNodes: (fn: (prev: WorkflowNode[]) => WorkflowNode[]) => void;
  workflowDetailRefetch: () => void;
  playbookCache: ReturnType<typeof usePlaybookCache>;

  // History banner
  previewingVersion: VersionSelection | null;
  previewSnapshotQ: { data: SnapshotDetail | null | undefined };
  previewRevisionQ: { data: WorkflowRevisionDetail | null | undefined };
  previewDraftSnapshotQ: { data: DraftSnapshotDetail | null | undefined };
  handleRestoreVersion: () => void;
  handleBackToCurrent: () => void;

  // Optimizing banner — non-null only while a cache convergence run is active
  optimization: ActiveOptimization;

  // Exploration banner
  explorationBanner: {
    isVisible: boolean;
    dismiss: () => void;
  };

  // Validation banner
  flowValidation: {
    isValidationActive: boolean;
    validationResult: any;
    currentErrorIndex: number;
    currentError: any;
    goToPrevError: () => void;
    goToNextError: () => void;
    dismissValidation: () => void;
  };
  setSelectedNodeForConfig: (node: WorkflowNode | null) => void;
  selectSidebarTab: (tab: SidebarTab) => void;

  // Version dropdown
  snapshots: SnapshotSummary[];
  revisions: WorkflowRevisionSummary[];
  draftSnapshots: DraftSnapshotSummary[];
  handleVersionSelect: (selection: VersionSelection) => void;
  onSaveVersion?: () => void;

  // Saving indicator
  isSaving: boolean;

  // API view
  flowView: FlowView;
  setFlowView: (view: FlowView) => void;
  hasSuccessfulRun: boolean;
  hasApiSequences: boolean;
  workflowApiOnly?: boolean;
  workflowPropertyId?: string | null;
  onApiOnlyToggle?: (apiOnly: boolean) => void;

  // Cache debug overlay
  cacheDebugRunId: string | null;
  onCloseCacheDebug: () => void;
};

export const FlowDesignerCanvas: React.FC<FlowDesignerCanvasProps> = ({
  canvasNodes,
  edges,
  nodeTypes,
  isPreviewingHistory,
  handleCanvasNodeClick,
  handleCanvasPaneClick,
  handleVerticalCanvasNodeDelete,
  handleEdgeClick,
  handleConnect,
  handlePrependNode,
  handleNodeReorder,
  selectedNodeIds,
  handleSelectionChange,
  handleMultiNodeDelete,
  handleMultiNodeReorder,
  handleOrphanInsertOnEdge,
  selectedEdgeId,
  currentStepId,
  selectedNodeForConfig,
  workflowStatus,
  workflowHealth,
  onReEnable,
  isReEnabling,
  isGenerating,
  workflowId,
  workflowCanvasTitle,
  workflowFlowType,
  generatedName,
  typingComplete,
  isWorkflowLoading,
  isSettingsOpen,
  setIsSettingsOpen,
  sandboxControl,
  sandboxV2Control,
  isScenarioConfigOpen,
  setIsScenarioConfigOpen,
  workflowInputs,
  workflowScenarios,
  credentialTemplates,
  onSaveInputs,
  isSavingInputs,
  onSaveScenarios,
  isSavingScenarios,
  isPlaybookOpen,
  setIsPlaybookOpen,
  playbookScrollToNodeId,
  setPlaybookScrollToNodeId,
  workflowName,
  workflowDescription,
  workflowPreflight,
  workflowCleanup,
  workflowRunAgentically,
  workflowSeedRunOncePerSuite,
  workflowCurrentStatus,
  workflowEnvironmentConstraints,
  workflowEligibleEnvironmentIds,
  workflowEnvironments,
  workflowDefaultEnvironmentId,
  workflowPassByEnvironment,
  workflowCreatedBy,
  workflowApprovedBy,
  workflowApprovedAt,
  workflowSource,
  runState,
  lifecycleState,
  runButtonStates,
  lifecycleButtonStates,
  isRunDisabled,
  isLifecycleDisabled,
  handleRunPress,
  handleLifecyclePress,
  handleRunWithConfig,
  hasRunConfig,
  eligibilityModel,
  workflowPaused,
  handlePause,
  handleUnpause,
  workflowIsApproved,
  workflowPromotion,
  setForcePromoteDialogOpen,
  onOptimizePress,
  isOptimizePending,
  cachePinnedAt,
  effectivePinned,
  isShadowFork,
  onPinToggle,
  isPinMutationPending,
  isSuggesting,
  canSuggest,
  isSubmittingSuggestion,
  onEnterSuggest,
  onDiscardSuggest,
  onSubmitSuggest,
  forkLineage,
  nodes,
  isInsertingNode,
  handleStandaloneCreateNode,
  createWorkflowMutationIsPending,
  hasApiCatalog,
  hasSandboxActions,
  hasMcp,
  autobuildEnabled,
  onAutobuildSubmit,
  isAutobuildSubmitting,
  edgeMenuPosition,
  clearEdgeSelection,
  handleInsertNodeOnEdge,
  handleDeleteSelectedEdge,
  workflowData: _workflowData,
  setNodes,
  workflowDetailRefetch,
  playbookCache,
  hintData,
  previewingVersion,
  previewSnapshotQ,
  previewRevisionQ,
  previewDraftSnapshotQ,
  handleRestoreVersion,
  handleBackToCurrent,
  optimization,
  explorationBanner,
  flowValidation,
  setSelectedNodeForConfig,
  selectSidebarTab,
  snapshots,
  revisions,
  draftSnapshots,
  handleVersionSelect,
  onSaveVersion,
  isSaving,
  flowView,
  setFlowView,
  hasSuccessfulRun,
  hasApiSequences,
  workflowApiOnly,
  workflowPropertyId,
  onApiOnlyToggle,
  cacheDebugRunId,
  onCloseCacheDebug,
}) => {
  const seedDependencies = React.useMemo(() => deriveSeedDependencies(nodes), [nodes]);

  // True when the browser flow is essentially a single API sequence — its only
  // executable node is an api_sequence node (mirrors the backend's
  // executable-node filter in adoptSingleApiSequenceNode). In that case the
  // "API only" switch is surfaced on the Browser tab so the flow can be flipped
  // into API-only mode without first opening the API tab.
  const isBrowserSingleApiSequence = React.useMemo(
    () => hasSingleApiSequenceExecutableNode(nodes),
    [nodes]
  );

  const [apiActions, setApiActions] = React.useState<ApiSequenceActions | null>(null);

  // Parked cache-convergence run awaiting a human answer (Decisions 11-12 of
  // `specs/pending/auto-maintain.md`). Only fetched while `optimization` says
  // the run is actually parked — the two banners are mutually exclusive.
  const isWaitingForFeedback = optimization?.status === 'waiting_for_feedback';
  const maintenanceFeedbackQ = useMaintenanceFeedbackQuery(workflowId, isWaitingForFeedback);
  const pendingFeedbackRequest = maintenanceFeedbackQ.data?.pending ?? null;

  // A workflow is actively being authored when either: (a) the DB status says
  // so, or (b) an agentic builder stream is live on the client. Either case
  // hides the empty-canvas picker + run controls and shows the interstitial.
  const parentStatusIsAuthoring = workflowStatus === 'generating' || isGenerating;
  const isEmptyExternalConversion =
    nodes.length === 0 && workflowSource?.provider === 'external-test';
  const generationEventsQ = useWorkflowGenerationEvents(workflowId, {
    enabled: parentStatusIsAuthoring || isEmptyExternalConversion,
    poll: parentStatusIsAuthoring,
  });
  const refetchGenerationEvents = generationEventsQ.refetch;
  const generationEvents = generationEventsQ.data?.events ?? [];
  const liveWorkflowStatus = generationEventsQ.data?.workflowStatus ?? workflowStatus;
  const isAuthoring = liveWorkflowStatus === 'generating' || isGenerating;
  const latestGenerationEvent = generationEvents[generationEvents.length - 1] ?? null;
  const hasFailedExternalConversion =
    isEmptyExternalConversion && latestGenerationEvent?.kind === 'failed' && !isAuthoring;
  const isLoadingExternalConversionState = isEmptyExternalConversion && generationEventsQ.isPending;
  const [isRetryingExternalConversion, setIsRetryingExternalConversion] = React.useState(false);
  const [externalConversionRetryError, setExternalConversionRetryError] = React.useState<
    string | null
  >(null);
  const retryExternalConversion = React.useCallback(async () => {
    if (!workflowId) return;
    setIsRetryingExternalConversion(true);
    setExternalConversionRetryError(null);
    try {
      const response = await fetch(
        `${API_URL}/org/workflows/${encodeURIComponent(workflowId)}/regenerate-from-external-test`,
        { method: 'POST', credentials: 'include' }
      );
      if (!response.ok) throw new Error('Failed to retry conversion');
      await Promise.all([refetchGenerationEvents(), workflowDetailRefetch()]);
    } catch (error) {
      setExternalConversionRetryError(
        error instanceof Error ? error.message : 'Failed to retry conversion'
      );
    } finally {
      setIsRetryingExternalConversion(false);
    }
  }, [refetchGenerationEvents, workflowDetailRefetch, workflowId]);

  // Flow Autobuild pipeline (spec review -> authoring -> converging ->
  // verifying -> completed). `isActive` stays true from the moment the
  // workflow is bound to a spec review through its terminal outcome —
  // `authoring` is deliberately excluded from the panel (see
  // AutobuildPipelinePanel's module doc) because GenerationInterstitial
  // already owns that overlay via `isAuthoring` above. The `converged`
  // terminal outcome IS shown (unlike `authoring`) — it's the user's only
  // chance to see the spec review's deviation blockers and the "View full
  // report" link, so it must render even though the canvas already has
  // nodes by the time a run converges. Dismissal is persisted per workflow
  // (see `readAutobuildPanelDismissed`/`writeAutobuildPanelDismissed` above)
  // so it doesn't reappear on every later visit to an already-converged flow.
  const autobuild = useFlowAutobuild(workflowId);
  const autobuildRetryMutation = useAutobuildRetryMutation(workflowId);
  const autobuildCancelMutation = useAutobuildCancelMutation(workflowId);
  const [autobuildDismissed, setAutobuildDismissed] = React.useState(() =>
    readAutobuildPanelDismissed(workflowId)
  );
  React.useEffect(() => {
    setAutobuildDismissed(readAutobuildPanelDismissed(workflowId));
  }, [workflowId]);
  const showAutobuildPanel = Boolean(autobuild.status) && !isAuthoring && !autobuildDismissed;

  // Detect the generating → done transition so we can refetch the workflow,
  // landing freshly-authored nodes/edges/cache status in the UI without a
  // manual reload. Guarded by a ref so we don't fire on every unrelated
  // status churn. We intentionally do NOT auto-run the workflow here — a
  // freshly-authored flow (including the Chrome-extension recording → builder
  // path) lands as a draft the user runs when ready.
  const wasAuthoringRef = React.useRef(isAuthoring);
  React.useEffect(() => {
    const wasAuthoring = wasAuthoringRef.current;
    wasAuthoringRef.current = isAuthoring;

    if (!wasAuthoring || isAuthoring) return;
    // Just transitioned from authoring → done. Refresh so the new nodes appear.
    workflowDetailRefetch();
  }, [isAuthoring, workflowDetailRefetch]);

  // cofactor.md spec — computed once, shared by the view and the canvas
  // copy/download controls (bottom-right, in line with the version dropdown).
  const flowSpec = useFlowSpec({
    enabled: flowView === 'spec',
    meta: _workflowData as SpecWorkflowMeta | null | undefined,
    name: workflowName,
    nodes,
    edges,
  });

  return (
    <div className="flex-1 flex overflow-hidden relative" role="main" aria-label="Flow canvas">
      {flowView === 'spec' && workflowId && flowSpec ? (
        <div className="flex-1 h-full relative min-w-0 min-h-0 pt-28">
          <FlowSpecView blocks={flowSpec.blocks} currentStepId={currentStepId} />
        </div>
      ) : flowView === 'api' && workflowId ? (
        <div className="flex-1 h-full relative flex flex-col pt-28 min-w-0 min-h-0">
          <ApiSequencePanel
            workflowId={workflowId}
            nodeCount={nodes.length}
            hasSuccessfulRun={hasSuccessfulRun}
            workflowFlowType={workflowFlowType}
            workflowApiOnly={workflowApiOnly}
            workflowPropertyId={workflowPropertyId}
            seedDependencies={seedDependencies}
            apiSequenceOverride={
              isPreviewingHistory && previewingVersion?.type === 'revision'
                ? (previewRevisionQ.data?.snapshotJson.apiSequence ?? null)
                : undefined
            }
            onActionsReady={setApiActions}
          />
        </div>
      ) : flowView === 'lineage' && workflowId ? (
        <div className="flex-1 h-full relative min-w-0 min-h-0">
          <FlowLineageCanvas workflowId={workflowId} workflowName={workflowName} />
        </div>
      ) : (
        <div className="flex-1 h-full relative">
          <VerticalFlowCanvas
            nodes={canvasNodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodeClick={handleCanvasNodeClick}
            onPaneClick={isPreviewingHistory ? undefined : handleCanvasPaneClick}
            onNodeDelete={isPreviewingHistory ? undefined : handleVerticalCanvasNodeDelete}
            onEdgeClick={isPreviewingHistory ? undefined : handleEdgeClick}
            onConnect={isPreviewingHistory ? undefined : handleConnect}
            onPrependNode={isPreviewingHistory ? undefined : handlePrependNode}
            onNodeReorder={isPreviewingHistory ? undefined : handleNodeReorder}
            selectedNodeIds={selectedNodeIds}
            onSelectionChange={isPreviewingHistory ? undefined : handleSelectionChange}
            onNodesDelete={isPreviewingHistory ? undefined : handleMultiNodeDelete}
            onNodesReorder={isPreviewingHistory ? undefined : handleMultiNodeReorder}
            onOrphanInsertOnEdge={isPreviewingHistory ? undefined : handleOrphanInsertOnEdge}
            selectedEdgeId={selectedEdgeId}
            currentStepId={currentStepId}
            focusedNodeId={selectedNodeForConfig?.id ?? null}
            isGenerating={isAuthoring}
          />

          {/* Fast spinner overlay during insert */}
          <AnimatePresence>
            {isInsertingNode && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.1 }}
                className="absolute inset-0 z-30 flex items-center justify-center bg-background/40 backdrop-blur-[2px]"
              >
                <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-background/90 border border-border shadow-lg">
                  <Spinner size="sm" className="text-primary animate-[spin_0.4s_linear_infinite]" />
                  <span className="text-sm font-medium text-muted-foreground">Inserting...</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Starter picker overlay for empty canvas */}
          <AnimatePresence>
            {nodes.length === 0 &&
              !isPreviewingHistory &&
              !isAuthoring &&
              !isLoadingExternalConversionState &&
              !hasFailedExternalConversion &&
              !showAutobuildPanel && (
                // `pt-28` clears the toolbar + view-tab strip that float `absolute
                // top-0` over this same region (the inset FlowSpecView already uses);
                // without it a tall card tucks its own header underneath them.
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 z-10 flex justify-center overflow-y-auto pt-28 pb-6"
                >
                  <StarterPicker
                    onSelectNodeType={handleStandaloneCreateNode}
                    isDisabled={!workflowId || createWorkflowMutationIsPending}
                    hasApiCatalog={hasApiCatalog}
                    autobuildEnabled={autobuildEnabled}
                    onAutobuildSubmit={onAutobuildSubmit}
                    isAutobuildSubmitting={isAutobuildSubmitting}
                  />
                </motion.div>
              )}
          </AnimatePresence>

          {/* Flow Autobuild pipeline overlay — spec review / converging / terminal outcome. Suppressed
              during `authoring` (GenerationInterstitial owns that phase, see AutobuildPipelinePanel doc). */}
          <AnimatePresence>
            {showAutobuildPanel && autobuild.status && (
              <AutobuildPipelinePanel
                status={autobuild.status}
                latestEvent={autobuild.latestEvent}
                onRetry={() => autobuildRetryMutation.mutate()}
                isRetrying={autobuildRetryMutation.isPending}
                retryError={
                  autobuildRetryMutation.error instanceof Error
                    ? autobuildRetryMutation.error.message
                    : null
                }
                onStartOver={() => setAutobuildDismissed(true)}
                onDismiss={() => {
                  setAutobuildDismissed(true);
                  writeAutobuildPanelDismissed(workflowId);
                  workflowDetailRefetch();
                }}
                onCancel={() => autobuildCancelMutation.mutate()}
                isCanceling={autobuildCancelMutation.isPending}
                hasNodes={nodes.length > 0}
              />
            )}
          </AnimatePresence>

          {/* Generation interstitial — shown while an author agent is building */}
          <AnimatePresence>
            {(isAuthoring || hasFailedExternalConversion) && (
              <GenerationInterstitial
                events={generationEvents}
                workflowStatus={liveWorkflowStatus}
                onRetry={hasFailedExternalConversion ? retryExternalConversion : undefined}
                isRetrying={isRetryingExternalConversion}
                retryError={externalConversionRetryError}
              />
            )}
          </AnimatePresence>
        </div>
      )}

      <FlowDesignerToolbar
        workflowId={workflowId}
        workflowCanvasTitle={workflowCanvasTitle}
        workflowFlowType={workflowFlowType}
        generatedName={generatedName}
        typingComplete={typingComplete}
        isWorkflowLoading={isWorkflowLoading}
        isSettingsOpen={isSettingsOpen}
        setIsSettingsOpen={setIsSettingsOpen}
        sandboxControl={sandboxControl}
        sandboxV2Control={sandboxV2Control}
        workflowHealth={workflowHealth}
        onReEnable={onReEnable}
        isReEnabling={isReEnabling}
        hintData={hintData}
        isSuggesting={isSuggesting}
        canSuggest={canSuggest}
        isSubmittingSuggestion={isSubmittingSuggestion}
        onEnterSuggest={onEnterSuggest}
        onDiscardSuggest={onDiscardSuggest}
        onSubmitSuggest={onSubmitSuggest}
        forkLineage={forkLineage}
        isPreviewingHistory={isPreviewingHistory}
        isAuthoring={isAuthoring}
        runState={runState}
        lifecycleState={lifecycleState}
        runButtonStates={runButtonStates}
        lifecycleButtonStates={lifecycleButtonStates}
        isRunDisabled={isRunDisabled}
        isLifecycleDisabled={isLifecycleDisabled}
        handleRunPress={handleRunPress}
        handleLifecyclePress={handleLifecyclePress}
        handleRunWithConfig={handleRunWithConfig}
        hasRunConfig={hasRunConfig}
        eligibilityModel={eligibilityModel}
        workflowPaused={workflowPaused}
        handlePause={handlePause}
        handleUnpause={handleUnpause}
        workflowIsApproved={workflowIsApproved}
        workflowPromotion={workflowPromotion}
        setForcePromoteDialogOpen={setForcePromoteDialogOpen}
        onOptimizePress={onOptimizePress}
        isOptimizePending={isOptimizePending}
        cachePinnedAt={cachePinnedAt}
        effectivePinned={effectivePinned}
        isShadowFork={isShadowFork}
        onPinToggle={onPinToggle}
        isPinMutationPending={isPinMutationPending}
      />

      {/* Hint toast */}
      {hintData.isOpen && (
        <div className="fixed top-14 left-16 z-50">
          <HintCards
            hints={hintData.hints}
            initialIndex={hintData.initialIndex}
            onDismissToday={() => hintData.dismiss('today')}
            onDismissForever={() => hintData.dismiss('forever')}
            onSeen={hintData.markSeen}
            onClose={hintData.close}
          />
        </div>
      )}

      {/* Local-dev hint: how to send real email when a notification node is present */}
      {IS_LOCAL && nodes.some((n) => n.type === 'notification') && <LocalEmailHintBanner />}

      {/* History preview banner */}
      {isPreviewingHistory &&
        (previewSnapshotQ.data || previewRevisionQ.data || previewDraftSnapshotQ.data) && (
          <HistoryBanner
            version={previewingVersion!}
            publishedAt={
              previewSnapshotQ.data?.publishedAt ??
              previewRevisionQ.data?.createdAt ??
              previewDraftSnapshotQ.data?.createdAt ??
              null
            }
            versionName={
              previewSnapshotQ.data?.name ??
              previewRevisionQ.data?.name ??
              previewDraftSnapshotQ.data?.name ??
              null
            }
            onRestore={handleRestoreVersion}
            onBackToCurrent={handleBackToCurrent}
          />
        )}

      {/* Optimizing banner — bottom-center, so it must never stack with HistoryBanner */}
      {optimization &&
        !isPreviewingHistory &&
        (isWaitingForFeedback && pendingFeedbackRequest && workflowId ? (
          <MaintenanceFeedbackBanner workflowId={workflowId} request={pendingFeedbackRequest} />
        ) : !isWaitingForFeedback ? (
          <OptimizingBanner optimization={optimization} />
        ) : null)}

      {/* Validation banner */}
      {flowValidation.isValidationActive && flowValidation.validationResult && (
        <ValidationBanner
          validationResult={flowValidation.validationResult}
          currentErrorIndex={flowValidation.currentErrorIndex}
          currentError={flowValidation.currentError}
          onPrev={() => {
            flowValidation.goToPrevError();
            const err =
              flowValidation.validationResult?.errors[
                flowValidation.currentErrorIndex > 0
                  ? flowValidation.currentErrorIndex - 1
                  : flowValidation.validationResult.errors.length - 1
              ];
            if (err?.nodeId) {
              const node = nodes.find((n) => n.id === err.nodeId);
              if (node) {
                setSelectedNodeForConfig(node);
                selectSidebarTab('config');
              }
            }
          }}
          onNext={() => {
            flowValidation.goToNextError();
            const err =
              flowValidation.validationResult?.errors[
                flowValidation.currentErrorIndex < flowValidation.validationResult.errors.length - 1
                  ? flowValidation.currentErrorIndex + 1
                  : 0
              ];
            if (err?.nodeId) {
              const node = nodes.find((n) => n.id === err.nodeId);
              if (node) {
                setSelectedNodeForConfig(node);
                selectSidebarTab('config');
              }
            }
          }}
          onDismiss={flowValidation.dismissValidation}
        />
      )}

      {/* Exploration banner */}
      {explorationBanner.isVisible && <ExplorationBanner onDismiss={explorationBanner.dismiss} />}

      <FlowDesignerViewControls
        workflowId={workflowId}
        isPreviewingHistory={isPreviewingHistory}
        isAuthoring={isAuthoring}
        flowView={flowView}
        setFlowView={setFlowView}
        hasApiSequences={hasApiSequences}
        workflowFlowType={workflowFlowType}
        workflowApiOnly={workflowApiOnly}
        onApiOnlyToggle={onApiOnlyToggle}
        isBrowserSingleApiSequence={isBrowserSingleApiSequence}
        apiActions={apiActions}
        flowSpec={flowSpec}
        snapshots={snapshots}
        revisions={revisions}
        draftSnapshots={draftSnapshots}
        previewingVersion={previewingVersion}
        handleVersionSelect={handleVersionSelect}
        onSaveVersion={onSaveVersion}
        isSaving={isSaving}
      />

      <FlowDesignerEdgeMenu
        selectedEdgeId={selectedEdgeId}
        edgeMenuPosition={edgeMenuPosition}
        hasApiCatalog={hasApiCatalog}
        hasSandboxActions={hasSandboxActions}
        hasMcp={hasMcp}
        clearEdgeSelection={clearEdgeSelection}
        handleInsertNodeOnEdge={handleInsertNodeOnEdge}
        handleDeleteSelectedEdge={handleDeleteSelectedEdge}
      />

      {/* Settings overlay */}
      <AnimatePresence>
        {isSettingsOpen && workflowId && (
          <WorkflowSettingsOverlay
            workflowId={workflowId}
            currentName={workflowName}
            currentDescription={workflowDescription}
            currentPreflight={workflowPreflight}
            currentCleanup={workflowCleanup}
            currentStatus={workflowCurrentStatus}
            currentPaused={(_workflowData?.pausedAt ?? null) != null}
            firstPassedAt={_workflowData?.firstPassedAt ?? null}
            currentPromotion={workflowPromotion}
            currentFlowType={workflowFlowType}
            currentRunAgentically={workflowRunAgentically}
            currentSeedRunOncePerSuite={workflowSeedRunOncePerSuite}
            createdBy={workflowCreatedBy}
            approvedBy={workflowApprovedBy}
            approvedAt={workflowApprovedAt}
            source={workflowSource}
            currentEnvironmentConstraints={workflowEnvironmentConstraints ?? null}
            eligibleEnvironmentIds={workflowEligibleEnvironmentIds ?? null}
            environments={workflowEnvironments ?? null}
            currentDefaultEnvironmentId={workflowDefaultEnvironmentId ?? null}
            currentViewportSize={_workflowData?.viewportSize ?? null}
            passByEnvironment={workflowPassByEnvironment ?? null}
            propertyId={(_workflowData?.environmentsPropertyId ?? null) as string | null}
            isOpen={isSettingsOpen}
            onClose={() => setIsSettingsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Scenario config overlay */}
      <AnimatePresence>
        {isScenarioConfigOpen && onSaveInputs && onSaveScenarios && (
          <ScenarioConfigOverlay
            isOpen={isScenarioConfigOpen}
            onClose={() => setIsScenarioConfigOpen(false)}
            inputs={workflowInputs ?? []}
            scenarios={workflowScenarios ?? []}
            credentialTemplates={credentialTemplates ?? []}
            onSaveInputs={onSaveInputs}
            onSaveScenarios={onSaveScenarios}
            isSavingInputs={isSavingInputs}
            isSavingScenarios={isSavingScenarios}
          />
        )}
      </AnimatePresence>

      {/* Playbook overlay */}
      <AnimatePresence>
        {isPlaybookOpen && (
          <PlaybookOverlay
            isOpen={isPlaybookOpen}
            onClose={() => {
              setIsPlaybookOpen(false);
              setPlaybookScrollToNodeId(null);
            }}
            nodes={nodes}
            edges={edges}
            scrollToNodeId={playbookScrollToNodeId}
            workflowId={workflowId}
            playbookCache={playbookCache}
            onCacheDeleted={() => {
              workflowDetailRefetch();
            }}
            onAllCachesDeleted={() => {
              setNodes((prev) =>
                prev.map((n) => ({
                  ...n,
                  data: {
                    ...n.data,
                    hasCachedReplay: false,
                    cachedAt: null,
                    cacheRequiresAgent: false,
                  },
                }))
              );
              workflowDetailRefetch();
            }}
            onNodeNavigate={(nodeId) => {
              setIsPlaybookOpen(false);
              setSelectedNodeForConfig(null);
              requestAnimationFrame(() => {
                const node = nodes.find((n) => n.id === nodeId);
                if (node) setSelectedNodeForConfig(node);
              });
            }}
          />
        )}
      </AnimatePresence>

      {/* Cache debug overlay */}
      <AnimatePresence>
        {cacheDebugRunId && workflowId && (
          <CacheDebugOverlay
            isOpen={!!cacheDebugRunId}
            onClose={onCloseCacheDebug}
            workflowId={workflowId}
            runId={cacheDebugRunId}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
